// Credits.js — /credits landing page (HoB credit system)
// Ported 1:1 from the client's HoBCreditsLanding.jsx (shadcn/ui + Tailwind).
// All styles are scoped under .hob-credits — see credits.css / tailwind.config.js.
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Slider } from "../components/ui/slider";
import { Separator } from "../components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  ClipboardList,
  Send,
  PenLine,
  CheckCircle2,
  Clapperboard,
  Check,
  LayoutDashboard,
  RefreshCw,
  Clock,
  Wallet,
  FolderLock,
  Eye,
  Handshake,
  Repeat,
} from "lucide-react";
import "./credits.css";
import logo from "../assets/img/logo2.png";

const BLUE = "#0388f7";

/* ---------------- Scroll reveal + progress ---------------- */

function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll(".reveal, .reveal-scale, .stagger");
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("in");
            io.unobserve(e.target);
          }
        }),
      { threshold: 0.1 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
}

function ProgressBar() {
  const ref = useRef(null);
  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement;
      const pct = (h.scrollTop / (h.scrollHeight - h.clientHeight)) * 100;
      if (ref.current) ref.current.style.width = pct + "%";
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <div
      ref={ref}
      className="fixed top-0 left-0 h-1 z-50 rounded-r-full"
      style={{ background: BLUE, width: 0 }}
    />
  );
}

/* ---------------- Data ---------------- */

const videoTiers = [
  {
    name: "Yellow",
    dot: "#ffcc00",
    cost: "0.3 credits",
    desc: "Simple edit with music, subtitles, basic color grading and basic graphics.",
  },
  {
    name: "Orange",
    dot: "#ff9500",
    cost: "0.5 credits",
    desc: "Structured edit with sound design, motion text, lower thirds, logos and simple transitions.",
  },
  {
    name: "Green",
    dot: "#28cd41",
    cost: "1.0 credit",
    desc: "Advanced edit with dynamic motion graphics, consistent color grading and enhanced sound.",
  },
  {
    name: "Blue",
    dot: "#0388f7",
    cost: "1.5 credits",
    desc: "Advanced edit with animations, visual effects and advanced audio post-production.",
  },
  {
    name: "Purple",
    dot: "#af52de",
    cost: "2.0 credits",
    desc: "AI-generated video with AI voiceover, color grading, finishing and sound mix.",
  },
];

const animationTiers = [
  {
    name: "Branded",
    dot: "#1d1d1f",
    cost: "1 credit",
    desc: "On-brand animations with motion elements, lower thirds and polished color.",
  },
  {
    name: "Small",
    dot: "#8e8e93",
    cost: "2 to 3 credits",
    desc: "Simple reel-style animation with clean motion and basic transitions.",
  },
  {
    name: "Medium",
    dot: "#5e5ce6",
    cost: "4 to 6 credits",
    desc: "More polished animation with smoother motion, refined timing and a clean, professional result.",
  },
  {
    name: "High-end",
    dot: "linear-gradient(135deg,#0388f7,#af52de)",
    cost: "7 to 10 credits",
    desc: "Premium animation with advanced motion design, complex scenes and top polish on every element.",
  },
];

const specialTiers = [
  {
    name: "Bigger projects",
    dot: "linear-gradient(135deg,#ffcc00,#ff9500,#af52de)",
    cost: "Quoted first",
    desc: "Long videos, commercials or heavy VFX. We quote these in credits first.",
  },
];

const steps = [
  {
    icon: ClipboardList,
    step: "STEP 1",
    title: "Pick your pack",
    desc: "Choose how many credits you want. We set up your folders, intake form, Frame.io and Slack.",
  },
  {
    icon: Send,
    step: "STEP 2",
    title: "Send your brief",
    desc: "Fill the intake form and upload your footage. We reply with the credit count before we start.",
  },
  {
    icon: PenLine,
    step: "STEP 3",
    title: "We make it",
    desc: "Our team edits, adds sound, motion and color. You follow progress live in your dashboard.",
  },
  {
    icon: CheckCircle2,
    step: "STEP 4",
    title: "Review and receive",
    desc: "Watch and comment in Frame.io. Two revision rounds are included. Approve it, and the files are yours.",
  },
];

const packs = [
  {
    name: "Starter Pack",
    credits: 10,
    tagline: "Try the workflow with a small volume.",
    sub: "3 month trial period",
    featured: false,
    features: [
      "Credits roll over across the trial",
      "Full setup: Drive, intake form, Frame.io, Slack",
      "Your own dashboard with credit log",
      "Two revision rounds per video",
    ],
    cta: "Start the trial",
  },
  {
    name: "Pro Pack",
    credits: 25,
    tagline: "For teams posting content every week.",
    sub: "Better price per credit",
    featured: true,
    features: [
      "Everything in Starter Pack",
      "Longer rollover window",
      "Priority scheduling",
      "Dedicated contact person",
    ],
    cta: "Talk to us",
  },
  {
    name: "Business Pack",
    credits: 50,
    tagline: "For brands with a serious content pipeline.",
    sub: "Even better price per credit",
    featured: false,
    features: [
      "Everything in Pro Pack",
      "Faster turnaround tier",
      "Monthly planning together",
      "Mixed content and animation",
    ],
    cta: "Talk to us",
  },
  {
    name: "VIP Pack",
    credits: 80,
    tagline: "HoB as your full backend post team.",
    sub: "Best price per credit",
    featured: false,
    features: [
      "Everything in Business Pack",
      "Top priority in the queue",
      "Quarterly strategy sessions",
      "First access to new services",
    ],
    cta: "Talk to us",
  },
];

const rules = [
  {
    icon: LayoutDashboard,
    tag: "Your dashboard",
    title: "Everything in one place",
    desc: "Every partner gets their own HoB dashboard with a credit log, project briefs and a calendar. You always see credits used, credits left and project status, live.",
  },
  {
    icon: Repeat,
    tag: "Revisions",
    title: "Two rounds are included",
    desc: "Every video includes two revision rounds. Need one extra internal round before client delivery? Just ask. More rounds after that cost credits, and we agree on it first.",
  },
  {
    icon: Clock,
    tag: "Delivery",
    title: "7 to 14 days, normally",
    desc: "Standard delivery is 7 to 14 days, depending on how much and how complex. Urgent deadlines are possible if you tell us early and we confirm.",
  },
  {
    icon: RefreshCw,
    tag: "Rollover",
    title: "Credits do not just disappear",
    desc: "Unused credits roll over during your plan period. Paid credits are non-refundable, so we help you plan them well.",
  },
  {
    icon: Wallet,
    tag: "Payment",
    title: "Paid first, then we produce",
    desc: "Plans are invoiced up front. Work starts once payment is confirmed. This keeps the queue fair for every partner.",
  },
  {
    icon: FolderLock,
    tag: "Your files",
    title: "Final videos are 100% yours",
    desc: "You get full and unlimited rights to every final video. Project source files stay with HoB, with a buy-out option if you want them. We warn you 30 days before any files are deleted.",
  },
  {
    icon: Eye,
    tag: "Portfolio",
    title: "We ask before we show",
    desc: "HoB may show finished work in our portfolio, but only after telling you first. Your end-client names stay private without written approval.",
  },
  {
    icon: Handshake,
    tag: "Trust",
    title: "We never touch your clients",
    desc: "HoB does not contact or work with your end-clients without your written approval. Your client relationships stay yours.",
  },
];

/* ---------------- Calculator logic ---------------- */

const round1 = (v) => Math.round(v * 10) / 10;

function buildMix(c) {
  const items = [];
  let left = c;

  if (c >= 60) { items.push({ name: "High-end animation", dot: "#5e5ce6", label: "1 × 7 cr", count: 1 }); left -= 7; }
  if (c >= 35) { items.push({ name: "Medium animation", dot: "#5e5ce6", label: "1 × 4 cr", count: 1 }); left -= 4; }
  if (c >= 18) { items.push({ name: "Small animation", dot: "#8e8e93", label: "1 × 2 cr", count: 1 }); left -= 2; }
  if (c >= 25) { items.push({ name: "Purple, AI video", dot: "#af52de", label: "1 × 2 cr", count: 1 }); left -= 2; }

  const blue = left >= 6 ? 2 : left >= 3 ? 1 : 0;
  if (blue) { items.push({ name: "Blue videos", dot: "#0388f7", label: `${blue} × 1.5 cr`, count: blue }); left -= blue * 1.5; }

  const green = Math.min(4, Math.floor(left * 0.35));
  if (green) { items.push({ name: "Green videos", dot: "#28cd41", label: `${green} × 1 cr`, count: green }); left -= green; }

  const orange = Math.min(8, Math.floor((left * 0.5) / 0.5));
  if (orange) { items.push({ name: "Orange videos", dot: "#ff9500", label: `${orange} × 0.5 cr`, count: orange }); left -= orange * 0.5; }

  const yellow = Math.floor(round1(left) / 0.3);
  if (yellow) { items.push({ name: "Yellow videos", dot: "#ffcc00", label: `${yellow} × 0.3 cr`, count: yellow }); }

  const total = items.reduce((s, i) => s + i.count, 0);
  return { items, total };
}

function packFor(c) {
  if (c <= 10) return { name: "Starter Pack", detail: "10 credits" };
  if (c <= 25) return { name: "Pro Pack", detail: "25 credits" };
  if (c <= 50) return { name: "Business Pack", detail: "50 credits" };
  return { name: "VIP Pack", detail: "80 credits" };
}

/* ---------------- Sections ---------------- */

function Nav() {
  return (
    <nav className="sticky top-0 z-40 border-b border-black/5 bg-white/80 backdrop-blur-xl">
      <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-3">
        <a href="#top" className="text-lg font-semibold tracking-tight">
          <img src={logo} alt="HoB Studio" className="h-10 w-auto" />
        </a>
        <Button asChild className="rounded-full px-5 transition-transform duration-200 hover:-translate-y-0.5">
          <a href="#start">Become a partner</a>
        </Button>
      </div>
    </nav>
  );
}

function Hero() {
  const included = ["Video editing", "Sound design", "Motion graphics", "Color grading", "Final delivery"];
  return (
    <header id="top" className="px-6 pb-24 pt-24 text-center md:pt-32">
      <div className="mx-auto max-w-3xl">
        <p className="reveal mb-4 text-sm font-semibold uppercase tracking-widest" style={{ color: BLUE }}>
          The HoB credit system
        </p>
        <h1 className="reveal text-5xl font-bold tracking-tight md:text-7xl">Credits made simple.</h1>
        <p className="reveal mx-auto mt-6 max-w-2xl text-xl text-muted-foreground md:text-2xl">
          You buy credits. You spend them on videos. That is the whole system. No hourly rates and no
          price talks for every project.
        </p>
      </div>

      <Card className="reveal-scale mx-auto mt-14 max-w-3xl overflow-hidden rounded-3xl shadow-none">
        <CardContent className="p-0">
          <div className="flex flex-wrap items-center justify-center gap-5 px-6 py-10">
            <div className="flex items-center gap-3 rounded-2xl bg-accent px-7 py-4">
              <span className="text-5xl font-bold leading-none" style={{ color: BLUE }}>1</span>
              <span className="text-left text-base font-semibold leading-tight">credit</span>
            </div>
            <span className="text-3xl font-light text-muted-foreground">=</span>
            <div className="flex items-center gap-3 rounded-2xl bg-zinc-900 px-7 py-4 text-white">
              <Clapperboard className="h-6 w-6 shrink-0" style={{ color: "#4da6ff" }} />
              <span className="text-left text-base font-semibold leading-tight">
                1 finished video,
                <br />
                ready to post
              </span>
            </div>
          </div>
          <Separator />
          <div className="flex flex-wrap justify-center gap-2 bg-muted/50 px-6 py-5">
            {included.map((x) => (
              <Badge
                key={x}
                variant="outline"
                className="gap-1.5 rounded-full bg-white px-3.5 py-1.5 text-sm font-medium transition-colors duration-200 hover:border-sky-500 hover:bg-sky-50"
              >
                <Check className="h-3.5 w-3.5" style={{ color: BLUE }} />
                {x}
              </Badge>
            ))}
          </div>
        </CardContent>
      </Card>
      <p className="reveal mt-5 text-sm text-muted-foreground">
        Simple videos cost less than one credit. Big work costs more. The credit score menu below shows
        exactly how much.
      </p>
    </header>
  );
}

function HowItWorks() {
  return (
    <section className="px-6 py-16 md:py-24">
      <div className="mx-auto max-w-5xl text-center">
        <p className="reveal mb-4 text-sm font-semibold uppercase tracking-widest" style={{ color: BLUE }}>
          How it works
        </p>
        <h2 className="reveal text-4xl font-bold tracking-tight md:text-5xl">Four easy steps.</h2>
        <p className="reveal mx-auto mt-4 max-w-2xl text-lg text-muted-foreground md:text-xl">
          Your team keeps the client relationship and creative direction. HoB supports the execution.
        </p>
        <div className="stagger mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {steps.map((s) => (
            <Card key={s.step} className="lift lift-blue rounded-3xl text-left">
              <CardContent className="p-7">
                <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-accent">
                  <s.icon className="h-6 w-6" style={{ color: BLUE }} />
                </div>
                <p className="mb-2 text-xs font-semibold tracking-widest" style={{ color: BLUE }}>
                  {s.step}
                </p>
                <h3 className="mb-2 text-lg font-semibold">{s.title}</h3>
                <p className="text-sm text-muted-foreground">{s.desc}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
}

function TierRow({ t }) {
  return (
    <div className="group flex items-center gap-5 rounded-2xl px-5 py-5 transition-colors duration-200 hover:bg-accent">
      <span className="h-4 w-4 shrink-0 rounded-full ring-4 ring-transparent transition-all duration-200 group-hover:ring-white" style={{ background: t.dot }} />
      <div className="min-w-0 flex-1 text-left">
        <p className="text-lg font-bold tracking-tight">{t.name}</p>
        <p className="mt-0.5 text-sm text-muted-foreground">{t.desc}</p>
      </div>
      <Badge className="shrink-0 rounded-full px-4 py-1.5 text-sm transition-transform duration-200 " style={{ background: BLUE }}>
        {t.cost}
      </Badge>
    </div>
  );
}

function CreditMenu() {
  return (
    <section className="px-6 pb-16 md:pb-24">
      <div className="mx-auto max-w-4xl text-center">
        <p className="reveal mb-4 text-sm font-semibold uppercase tracking-widest" style={{ color: BLUE }}>
          What credits buy
        </p>
        <h2 className="reveal text-4xl font-bold tracking-tight md:text-5xl">The credit score menu.</h2>
        <p className="reveal mx-auto mt-4 max-w-2xl text-lg text-muted-foreground md:text-xl">
          Every video gets a color and a score. Small edits cost less than one credit. Big animations
          cost more. You always know the score before we start.
        </p>

        <Tabs defaultValue="video" className="reveal-scale mt-12">
          <TabsList className="mx-auto grid h-auto w-full max-w-md grid-cols-3 rounded-full bg-muted p-1.5">
            <TabsTrigger value="video" className="rounded-full py-2 text-sm font-medium transition-all data-[state=active]:shadow-md">
              Video content
            </TabsTrigger>
            <TabsTrigger value="animation" className="rounded-full py-2 text-sm font-medium transition-all data-[state=active]:shadow-md">
              Animation
            </TabsTrigger>
            <TabsTrigger value="special" className="rounded-full py-2 text-sm font-medium transition-all data-[state=active]:shadow-md">
              Special
            </TabsTrigger>
          </TabsList>

          <TabsContent value="video">
            <Card className="mt-6 rounded-3xl">
              <CardContent className="divide-y p-2 md:p-3">
                {videoTiers.map((t) => (
                  <TierRow key={t.name} t={t} />
                ))}
              </CardContent>
            </Card>
          </TabsContent>
          <TabsContent value="animation">
            <Card className="mt-6 rounded-3xl">
              <CardContent className="divide-y p-2 md:p-3">
                {animationTiers.map((t) => (
                  <TierRow key={t.name} t={t} />
                ))}
              </CardContent>
            </Card>
          </TabsContent>
          <TabsContent value="special">
            <Card className="mt-6 rounded-3xl">
              <CardContent className="divide-y p-2 md:p-3">
                {specialTiers.map((t) => (
                  <TierRow key={t.name} t={t} />
                ))}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <p className="reveal mt-5 text-sm text-muted-foreground">
          Your agreement includes a Credit Specification Document with the exact score for every job type.
        </p>
      </div>
    </section>
  );
}

function Calculator() {
  const [credits, setCredits] = useState(25);
  const mix = buildMix(credits);
  const pack = packFor(credits);

  return (
    <section className="bg-black px-6 py-20 text-white md:py-28">
      <div className="mx-auto max-w-4xl text-center">
        <p className="reveal mb-4 text-sm font-semibold uppercase tracking-widest" style={{ color: "#4da6ff" }}>
          Try it
        </p>
        <h2 className="reveal text-4xl font-bold tracking-tight md:text-5xl">How far do your credits go?</h2>
        <p className="reveal mx-auto mt-4 max-w-2xl text-lg text-zinc-400 md:text-xl">
          Move the slider. See what a month of content could look like.
        </p>

        <Card className="reveal-scale mt-12 rounded-3xl border-zinc-800 bg-zinc-900 text-white">
          <CardContent className="p-8 md:p-12">
            <div className="flex flex-wrap items-baseline justify-center gap-4">
              <span className="text-7xl font-bold tracking-tight tabular-nums md:text-8xl">{credits}</span>
              <span className="text-2xl font-medium text-zinc-400">credits</span>
            </div>

            <div className="mt-10">
              <Slider
                value={[credits]}
                onValueChange={(v) => setCredits(v[0])}
                min={10}
                max={80}
                step={1}
                aria-label="Credit volume"
                className="cursor-pointer [&_[role=slider]]:h-7 [&_[role=slider]]:w-7 [&_[role=slider]]:border-4 [&_[role=slider]]:border-white [&_[role=slider]]:bg-sky-500 [&_[role=slider]]:shadow-lg [&_[role=slider]]:transition-transform [&_[role=slider]]:hover:scale-105"
              />
              <div className="mt-3 flex justify-between text-xs text-zinc-500">
                <span>10</span>
                <span>45</span>
                <span>80</span>
              </div>
            </div>

            <div className="mt-10 grid gap-5 text-left md:grid-cols-2">
              <Card className="rounded-2xl border-zinc-800 bg-zinc-950 text-white">
                <CardContent className="p-6">
                  <p className="mb-4 text-xs font-semibold uppercase tracking-widest text-zinc-500">
                    Could look like
                  </p>
                  <ul className="divide-y divide-zinc-800">
                    {mix.items.map((i) => (
                      <li key={i.name} className="flex items-center justify-between gap-4 py-2.5 text-sm text-zinc-300">
                        <span className="flex items-center gap-2.5">
                          <span className="h-2.5 w-2.5 rounded-full" style={{ background: i.dot }} />
                          {i.name}
                        </span>
                        <span className="font-semibold" style={{ color: "#4da6ff" }}>{i.label}</span>
                      </li>
                    ))}
                    <li className="flex items-center justify-between gap-4 py-2.5 text-sm font-semibold text-white">
                      <span>Finished videos, about</span>
                      <span style={{ color: "#4da6ff" }}>{mix.total}</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="rounded-2xl border-zinc-800 bg-zinc-950 text-white">
                <CardContent className="p-6">
                  <p className="mb-4 text-xs font-semibold uppercase tracking-widest text-zinc-500">
                    Fits your pack
                  </p>
                  <p className="text-base text-zinc-200">
                    This volume fits the <strong style={{ color: "#4da6ff" }}>{pack.name}</strong> with {pack.detail}.
                  </p>
                  <ul className="mt-4 divide-y divide-zinc-800">
                    <li className="flex items-center justify-between gap-4 py-2.5 text-sm text-zinc-300">
                      <span>Unused credits</span>
                      <span className="font-semibold" style={{ color: "#4da6ff" }}>roll over</span>
                    </li>
                    <li className="flex items-center justify-between gap-4 py-2.5 text-sm text-zinc-300">
                      <span>Credit score before we start</span>
                      <span className="font-semibold" style={{ color: "#4da6ff" }}>always</span>
                    </li>
                    <li className="flex items-center justify-between gap-4 py-2.5 text-sm text-zinc-300">
                      <span>Credit log and briefs</span>
                      <span className="font-semibold" style={{ color: "#4da6ff" }}>live in your dashboard</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </CardContent>
        </Card>
      </div>
    </section>
  );
}

function Packs() {
  return (
    <section id="plans" className="px-6 py-20 md:py-28">
      <div className="mx-auto max-w-6xl text-center">
        <p className="reveal mb-4 text-sm font-semibold uppercase tracking-widest" style={{ color: BLUE }}>
          Packs
        </p>
        <h2 className="reveal text-4xl font-bold tracking-tight md:text-5xl">Pick your pack.</h2>
        <p className="reveal mx-auto mt-4 max-w-2xl text-lg text-muted-foreground md:text-xl">
          Same team and same quality in every pack. The only difference is how much you produce.
        </p>

        <div className="stagger mt-16 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {packs.map((p) => (
            <Card
              key={p.name}
              className={`lift relative flex flex-col rounded-3xl text-left ${
                p.featured ? "border-2 shadow-2xl" : "lift-blue"
              }`}
              style={p.featured ? { borderColor: BLUE } : undefined}
            >
              {p.featured && (
                <Badge
                  className="absolute -top-3.5 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full px-4 py-1 text-xs uppercase tracking-wider"
                  style={{ background: BLUE }}
                >
                  Most popular
                </Badge>
              )}
              <CardContent className="flex flex-1 flex-col p-7">
                <h3 className="text-xl font-bold">{p.name}</h3>
                <p className="mt-1 min-h-10 text-sm text-muted-foreground">{p.tagline}</p>
                <p className="mt-5 text-5xl font-bold tracking-tight">
                  {p.credits} <span className="text-base font-medium text-muted-foreground">credits</span>
                </p>
                <p className="mt-1 text-sm text-muted-foreground">{p.sub}</p>
                <ul className="mt-6 flex-1 space-y-0">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 border-b py-2.5 text-sm last:border-0">
                      <Check className="mt-0.5 h-4 w-4 shrink-0" style={{ color: BLUE }} />
                      {f}
                    </li>
                  ))}
                </ul>
                <Button
                  asChild
                  variant={p.featured ? "default" : "outline"}
                  className={`mt-7 w-full rounded-full transition-all duration-200 hover:-translate-y-0.5 ${
                    p.featured ? "" : "border-sky-600 text-sky-600 hover:bg-sky-50 hover:text-sky-700"
                  }`}
                >
                  <a href="#start">{p.cta}</a>
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
        <p className="reveal mt-7 text-sm text-muted-foreground">
          Pricing is agreed in your partner agreement and invoiced up front. Need more than 80 credits?
          Ask us for a custom pack.
        </p>
      </div>
    </section>
  );
}

function Rules() {
  return (
    <section className="px-6 pb-20 md:pb-28">
      <div className="mx-auto max-w-5xl text-center">
        <p className="reveal mb-4 text-sm font-semibold uppercase tracking-widest" style={{ color: BLUE }}>
          The fine print, up front
        </p>
        <h2 className="reveal text-4xl font-bold tracking-tight md:text-5xl">Clear rules.</h2>
        <p className="reveal mx-auto mt-4 max-w-2xl text-lg text-muted-foreground md:text-xl">
          These are the same rules our partners sign. Simple on purpose.
        </p>
        <div className="stagger mt-14 grid gap-5 md:grid-cols-2">
          {rules.map((r) => (
            <Card key={r.tag} className="lift lift-blue rounded-3xl text-left">
              <CardContent className="p-7">
                <div className="mb-4 flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-accent">
                    <r.icon className="h-5 w-5" style={{ color: BLUE }} />
                  </div>
                  <Badge variant="secondary" className="rounded-full text-xs font-semibold uppercase tracking-wider" style={{ color: BLUE }}>
                    {r.tag}
                  </Badge>
                </div>
                <h3 className="mb-2 text-lg font-semibold">{r.title}</h3>
                <p className="text-sm text-muted-foreground">{r.desc}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
}

function Cta() {
  return (
    <section id="start" className="bg-gradient-to-b from-background to-sky-50 px-6 py-24 text-center md:py-32">
      <div className="mx-auto max-w-2xl">
        <h2 className="reveal text-4xl font-bold tracking-tight md:text-5xl">Start with one project.</h2>
        <p className="reveal mt-5 text-lg text-muted-foreground md:text-xl">
          We can start with one project to test the workflow in practice. If it fits, you pick a pack.
          The goal is to make the collaboration simple and reliable.
        </p>
        <Button
          asChild
          size="lg"
          className="reveal mt-10 rounded-full px-10 py-6 text-base transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md"
        >
          <a href="mailto:info@hobstudio.tv?subject=Partner%20inquiry%20HoB%20credit%20system">
            Request the credit catalogue
          </a>
        </Button>
        <p className="reveal mt-5 text-sm text-muted-foreground">
          info@hobstudio.tv &nbsp;·&nbsp; +41 76 699 01 64
        </p>
      </div>
    </section>
  );
}

function CreditsFooter() {
  return (
    <footer className="border-t px-6 py-10">
      <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-4 text-xs text-muted-foreground">
        <span>© House of Berechet L.L.C. All rights reserved.</span>
        <span>
          Rr. Ukshin Hoti, Prime 2, Prishtinë, Kosova &nbsp;·&nbsp;{" "}
          <a href="mailto:info@hobstudio.tv" className="transition-colors hover:text-foreground">
            info@hobstudio.tv
          </a>{" "}
          &nbsp;·&nbsp; +41 76 699 01 64
        </span>
      </div>
    </footer>
  );
}

export default function Credits() {
  useReveal();
  // Bootstrap (loaded via CDN in index.html) defines !important utilities like
  // .px-5/.py-5/.gap-5 that clash with Tailwind's. This page renders without the
  // site chrome, so switch Bootstrap off while it's mounted and back on when
  // navigating away.
  useLayoutEffect(() => {
    const links = Array.from(
      document.querySelectorAll('link[rel="stylesheet"]')
    ).filter((l) => /bootstrap/i.test(l.href));
    links.forEach((l) => {
      l.disabled = true;
    });
    return () =>
      links.forEach((l) => {
        l.disabled = false;
      });
  }, []);
  useEffect(() => {
    const prev = document.title;
    document.title = "HoB Studio credits, explained simply";
    return () => {
      document.title = prev;
    };
  }, []);
  return (
    <div className="hob-credits min-h-screen">
      <ProgressBar />
      <Nav />
      <Hero />
      <HowItWorks />
      <CreditMenu />
      <Calculator />
      <Packs />
      <Rules />
      <Cta />
      <CreditsFooter />
    </div>
  );
}
